import React, { useContext } from 'react';
import { AppContext } from '../../App';
import { Users, DollarSign, Car, CreditCard } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const DemographicsGrid = () => {
  const { dashboardData } = useContext(AppContext);
  
  if (!dashboardData) return null;
  
  const { demographics } = dashboardData;

  const COLORS = ['#3B82F6', '#F97316', '#22C55E', '#EF4444', '#A855F7', '#EC4899'];

  const formatData = (data) => {
    return Object.entries(data).map(([name, value]) => ({
      name,
      value: parseFloat(value)
    }));
  };

  const ageData = formatData(demographics.age_groups);
  const preferenceData = formatData(demographics.buyer_preferences);
  const financingData = formatData(demographics.financing_trends);
  const incomeData = formatData(demographics.income_brackets);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass p-3 rounded-lg">
          <p className="text-white text-sm font-medium">{payload[0].name}</p>
          <p className="text-[#3B82F6] text-sm font-mono">{payload[0].value.toFixed(1)}%</p>
        </div>
      );
    }
    return null;
  };

  // Find top values
  const topAge = ageData.reduce((a, b) => a.value > b.value ? a : b);
  const topPreference = preferenceData.reduce((a, b) => a.value > b.value ? a : b);
  const topFinancing = financingData.reduce((a, b) => a.value > b.value ? a : b);
  const topIncome = incomeData.reduce((a, b) => a.value > b.value ? a : b);

  return (
    <div className="dashboard-card p-6" data-testid="demographics">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-[#A855F7]/10 rounded-lg">
          <Users className="w-5 h-5 text-[#A855F7]" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Buyer Demographics</h3>
          <p className="text-sm text-[#A1A1AA]">Active buyer trends &amp; preferences</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="p-3 bg-[#27272A]/50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-[#3B82F6]" />
            <span className="text-xs text-[#A1A1AA]">Top Age Group</span>
          </div>
          <p className="text-lg font-bold text-white">{topAge.name}</p>
          <p className="text-xs text-[#3B82F6]">{topAge.value.toFixed(1)}% of buyers</p>
        </div>
        <div className="p-3 bg-[#27272A]/50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Car className="w-4 h-4 text-[#F97316]" />
            <span className="text-xs text-[#A1A1AA]">Top Preference</span>
          </div>
          <p className="text-lg font-bold text-white">{topPreference.name}</p>
          <p className="text-xs text-[#F97316]">{topPreference.value.toFixed(1)}% prefer</p>
        </div>
        <div className="p-3 bg-[#27272A]/50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-[#22C55E]" />
            <span className="text-xs text-[#A1A1AA]">Top Income</span>
          </div>
          <p className="text-lg font-bold text-white">{topIncome.name}</p>
          <p className="text-xs text-[#22C55E]">{topIncome.value.toFixed(1)}% bracket</p>
        </div>
        <div className="p-3 bg-[#27272A]/50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-[#A855F7]" />
            <span className="text-xs text-[#A1A1AA]">Top Financing</span>
          </div>
          <p className="text-lg font-bold text-white">{topFinancing.name}</p>
          <p className="text-xs text-[#A855F7]">{topFinancing.value.toFixed(1)}% use</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Age Distribution */}
        <div>
          <h4 className="text-sm font-medium text-[#A1A1AA] mb-3">Age Distribution</h4>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={ageData}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={55}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {ageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {ageData.slice(0, 4).map((item, index) => (
              <div key={index} className="flex items-center gap-1 text-xs">
                <div className="w-2 h-2 rounded" style={{ backgroundColor: COLORS[index] }} />
                <span className="text-[#A1A1AA]">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Vehicle Preferences */}
        <div>
          <h4 className="text-sm font-medium text-[#A1A1AA] mb-3">Vehicle Preferences</h4>
          <div className="space-y-2">
            {preferenceData.slice(0, 5).map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[#A1A1AA]">{item.name}</span>
                  <span className="text-white font-mono">{item.value.toFixed(1)}%</span>
                </div>
                <div className="w-full h-2 bg-[#27272A] rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${(item.value / 50) * 100}%`,
                      backgroundColor: COLORS[index % COLORS.length]
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemographicsGrid;
